import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, type LoaderFunctionArgs } from 'react-router-dom';
import { cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import {
  ApiKeyRequestOutcomeSchema,
  type ApiCallListItem,
  type ApiKeyRequestOutcomeDto,
  type ListApiCallsQuery,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ClockIcon, KeyIcon, TagIcon, TextIcon, WorkspacesIcon } from '@/components/layout/icons';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { CommitOnBlurInput } from '@/components/commit-on-blur-input';
import { ListEmptyState, TestWorkspacesPill, WorkspaceLabel } from '@/components/test-workspaces';
import { readDateParam, readEnumParam, readUuidParam, useListParams } from '@/lib/list-view/use-list-params';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';

const OUTCOMES: ReadonlyArray<ApiKeyRequestOutcomeDto> = [
  'success',
  'denied',
  'rate-limited',
  'client-error',
  'server-error',
  'auth-failed',
];

const METHODS: ReadonlyArray<string> = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The sentinel the two id inputs accept instead of a uuid. It maps to the
// contract's `*Unresolved` flag rather than being sent as an id — those rows
// have no id to match on, which is the whole reason the flag exists.
const UNRESOLVED = 'unresolved';

type LoaderData = {
  readonly requests: ReadonlyArray<ApiCallListItem>;
  readonly nextCursor: string | null;
  readonly columnPreference: ColumnPreference;
};

function toIso(localValue: string): string | null {
  if (localValue === '') return null;
  const date = new Date(localValue);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function isoToLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Pins `credentialClass` to `api-key`: this page is the call log narrowed to
// key-authenticated traffic, not a second store (IMM-494.3).
// The test-workspace flag is NOT read from the URL: it is the shared,
// browser-persisted filter (IMM-593.2), spread in at each call site from
// `includeTestWorkspacesQuery()`.
type ListApiCallsFilters = Omit<ListApiCallsQuery, 'includeTestWorkspaces'>;

export function queryFromParams(searchParams: URLSearchParams): ListApiCallsFilters {
  const query: Record<string, unknown> = { credentialClass: 'api-key' };
  const workspaceParam = searchParams.get('workspaceId');
  const apiKeyParam = searchParams.get('apiKeyId');
  const workspaceUuid = readUuidParam(searchParams, 'workspaceId');
  const apiKeyUuid = readUuidParam(searchParams, 'apiKeyId');
  const method = searchParams.get('method');
  const outcome = readEnumParam(searchParams, 'outcome', ApiKeyRequestOutcomeSchema.options);
  const endpoint = searchParams.get('endpoint');
  const from = readDateParam(searchParams, 'from');
  const to = readDateParam(searchParams, 'to');

  // `unresolved` is a UI sentinel, never an id: sending both would be refused by
  // the contract's refine, so exactly one branch is written per dimension.
  if (workspaceParam === UNRESOLVED) query.workspaceUnresolved = true;
  else if (workspaceUuid !== null) query.workspaceId = workspaceUuid;

  if (apiKeyParam === UNRESOLVED) query.apiKeyUnresolved = true;
  else if (apiKeyUuid !== null) query.apiKeyId = apiKeyUuid;

  if (method) query.method = method;

  if (outcome !== null) query.outcome = outcome;

  if (endpoint) query.endpoint = endpoint;

  // The contract wants a full ISO datetime; a parseable hand-edited date is normalised here, never in the address.
  if (from !== null) query.from = new Date(from).toISOString();

  if (to !== null) query.to = new Date(to).toISOString();

  return query as ListApiCallsFilters;
}

export const apiKeyRequestsLoaderParams = [
  'workspaceId',
  'apiKeyId',
  'method',
  'outcome',
  'endpoint',
  'from',
  'to',
] as const;

export async function apiKeyRequestsLoader({ request }: LoaderFunctionArgs): Promise<LoaderData> {
  const query = queryFromParams(new URL(request.url).searchParams);

  const [result, columnPreference] = await Promise.all([
    apiClient.adminApiCalls.list({ query: { ...query, ...includeTestWorkspacesQuery() } }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.apiKeyRequests),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load API key requests', { status: result.status });
  }

  return { requests: result.body.data, nextCursor: result.body.nextCursor, columnPreference };
}

const idFilterValue = (searchParams: URLSearchParams, key: 'workspaceId' | 'apiKeyId'): string =>
  searchParams.get(key) === UNRESOLVED ? UNRESOLVED : (readUuidParam(searchParams, key) ?? '');

function labelFor(name: string | null, id: string | null): string | null {
  if (name !== null && name !== '') return name;

  if (id !== null && id !== '') return id;

  return null;
}

// The width-less columns (Workspace, API-Key, Endpunkt) grow with their data: the icon heads overflow
// `main` by 187px at 1280px and still by 27px at 1440px, and only fit from ~1570px, so labels show
// from 1920px and the head's `title` carries them below. Written as a whole literal so Tailwind emits
// it (BL-034, BL-065; IMM-1074.7 plan).
const LABEL_FROM_1920 = 'hidden min-[1920px]:inline';

function useApiKeyRequestColumns(): ReadonlyArray<OperatorColumn<ApiCallListItem>> {
  const t = useTranslations('pages.admin.api-key-requests');
  const tReplay = useTranslations('pages.admin.api-keys.replay');
  const intl = useIntl();

  return useMemo(() => {
    const placeholder = t('placeholder');

    return [
      {
        id: 'occurredAt',
        label: t('columns.occurred-at'),
        icon: ClockIcon,
        headClassName: 'w-[150px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (row) => intl.formatDate(row.occurredAt, { dateStyle: 'short', timeStyle: 'short' }),
        sortValue: (row) => Date.parse(row.occurredAt),
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: (row) => (
          <WorkspaceLabel isTest={row.isTest}>
            {labelFor(row.workspaceName, row.workspaceId) ?? placeholder}
          </WorkspaceLabel>
        ),
        sortValue: (row) => labelFor(row.workspaceName, row.workspaceId),
      },
      {
        id: 'apiKey',
        label: t('columns.api-key'),
        icon: KeyIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: (row) => labelFor(row.apiKeyName, row.apiKeyId) ?? placeholder,
        sortValue: (row) => labelFor(row.apiKeyName, row.apiKeyId),
      },
      {
        id: 'endpoint',
        label: t('columns.endpoint'),
        icon: TextIcon,
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'font-mono text-sm',
        cell: (row) => (
          <>
            <span className="text-text-tertiary">{row.method}</span> {row.endpoint}
          </>
        ),
        sortValue: (row) => `${row.method} ${row.endpoint}`,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[80px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'tabular-nums',
        cell: (row) => row.statusCode,
        sortValue: (row) => row.statusCode,
      },
      {
        id: 'outcome',
        label: t('columns.outcome'),
        icon: TagIcon,
        headClassName: 'w-[190px]',
        headLabelClassName: LABEL_FROM_1920,
        cell: (row) => t(`outcomes.${row.outcome}`),
        sortValue: (row) => t(`outcomes.${row.outcome}`),
      },
      {
        id: 'replay',
        label: t('columns.replay'),
        icon: ClockIcon,
        headClassName: 'w-[150px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'text-sm text-text-tertiary',
        // Recovery is worked as a queue, so the last attempt belongs
        // on the row — an operator going through a backlog needs to
        // see which entries are already handled without opening each.
        cell: (row) =>
          row.lastReplay === null
            ? placeholder
            : row.lastReplay.outcome === null
              ? tReplay('in-flight')
              : intl.formatDate(row.lastReplay.attemptedAt, { dateStyle: 'short', timeStyle: 'short' }),
        sortValue: (row) => (row.lastReplay === null ? null : Date.parse(row.lastReplay.attemptedAt)),
      },
    ];
  }, [t, tReplay, intl]);
}

export function ApiKeyRequestsPage() {
  const t = useTranslations('pages.admin.api-key-requests');
  const navigate = useNavigate();
  const { requests, nextCursor, columnPreference } = useLoaderData() as LoaderData;
  const { params: searchParams, setParams } = useListParams();

  // Accumulate keyset pages client-side: the loader gives page 1 (and resets the
  // list whenever the filters change), "Mehr laden" appends the next page.
  const [rows, setRows] = useState<ReadonlyArray<ApiCallListItem>>(requests);
  const [cursor, setCursor] = useState<string | null>(nextCursor);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setRows(requests);
    setCursor(nextCursor);
  }, [requests, nextCursor]);

  const catalogue = useApiKeyRequestColumns();
  const table = useOperatorTable({
    rows,
    preferenceKey: OPERATOR_COLUMN_KEYS.apiKeyRequests,
    catalogue,
    alwaysVisibleId: 'occurredAt',
    loadedPreference: columnPreference,
  });

  const selectedOutcome = readEnumParam(searchParams, 'outcome', ApiKeyRequestOutcomeSchema.options);
  const selectedMethod = searchParams.get('method');

  function toggleOutcome(outcome: ApiKeyRequestOutcomeDto): void {
    setParams({ outcome: selectedOutcome === outcome ? null : outcome });
  }

  function toggleMethod(method: string): void {
    setParams({ method: selectedMethod === method ? null : method });
  }

  // Accepts a uuid or the `unresolved` sentinel; anything else clears the param
  // rather than 400-ing the loader.
  function commitIdFilter(key: 'workspaceId' | 'apiKeyId', value: string): void {
    const trimmed = value.trim().toLowerCase();

    if (trimmed === UNRESOLVED) setParams({ [key]: UNRESOLVED });
    else setParams({ [key]: UUID_RE.test(trimmed) ? trimmed : null });
  }

  function toggleUnresolved(key: 'workspaceId' | 'apiKeyId'): void {
    setParams({ [key]: searchParams.get(key) === UNRESOLVED ? null : UNRESOLVED });
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) return;
    setLoadingMore(true);

    try {
      const result = await apiClient.adminApiCalls.list({
        query: { ...queryFromParams(searchParams), ...includeTestWorkspacesQuery(), cursor },
      });

      if (result.ok) {
        setRows((prev) => [...prev, ...result.body.data]);
        setCursor(result.body.nextCursor);
      }
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={rows.length} icon={KeyIcon} />} />
      <div className="space-y-1 border-b border-border bg-surface px-6 py-3">
        <p className="text-sm text-text-secondary">{t('subtitle')}</p>
      </div>
      <FilterBar
        pills={
          <>
            {OUTCOMES.map((outcome) => (
              <FilterPill
                key={outcome}
                icon={TagIcon}
                label={t(`outcomes.${outcome}`)}
                active={selectedOutcome === outcome}
                onToggle={() => toggleOutcome(outcome)}
              />
            ))}
            <TestWorkspacesPill />
          </>
        }
        searchValue={searchParams.get('endpoint') ?? ''}
        searchPlaceholder={t('filters.endpoint-placeholder')}
        onSearchChange={(next, commit) => setParams({ endpoint: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-border bg-surface px-4 py-2">
        <div className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.method')}
          <div className="flex flex-wrap gap-1.5">
            {METHODS.map((method) => (
              <FilterPill
                key={method}
                icon={TextIcon}
                label={method}
                active={selectedMethod === method}
                onToggle={() => toggleMethod(method)}
              />
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.workspace')}
          <CommitOnBlurInput
            className="h-8 w-72"
            value={idFilterValue(searchParams, 'workspaceId')}
            placeholder={t('filters.workspace-placeholder')}
            aria-label={t('filters.workspace')}
            onCommit={(next) => commitIdFilter('workspaceId', next)}
          />
        </label>
        <FilterPill
          icon={WorkspacesIcon}
          label={t('filters.workspace-unresolved')}
          active={searchParams.get('workspaceId') === UNRESOLVED}
          onToggle={() => toggleUnresolved('workspaceId')}
        />
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.api-key')}
          <CommitOnBlurInput
            className="h-8 w-72"
            value={idFilterValue(searchParams, 'apiKeyId')}
            placeholder={t('filters.api-key-placeholder')}
            aria-label={t('filters.api-key')}
            onCommit={(next) => commitIdFilter('apiKeyId', next)}
          />
        </label>
        <FilterPill
          icon={KeyIcon}
          label={t('filters.api-key-unresolved')}
          active={searchParams.get('apiKeyId') === UNRESOLVED}
          onToggle={() => toggleUnresolved('apiKeyId')}
        />
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.from')}
          <CommitOnBlurInput
            type="datetime-local"
            className="h-8"
            aria-label={t('filters.from')}
            value={isoToLocalInput(readDateParam(searchParams, 'from'))}
            onCommit={(next) => setParams({ from: toIso(next) })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.to')}
          <CommitOnBlurInput
            type="datetime-local"
            className="h-8"
            aria-label={t('filters.to')}
            value={isoToLocalInput(readDateParam(searchParams, 'to'))}
            onCommit={(next) => setParams({ to: toIso(next) })}
          />
        </label>
      </div>
      {rows.length === 0 ? (
        <ListEmptyState title={t('empty')} />
      ) : (
        <>
          <Table flush>
            <TableHeader>
              <TableRow {...table.columns.headerRowProps}>
                {table.columns.visibleColumns.map((column) => (
                  <OperatorColumnHead key={column.id} column={column} table={table} />
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.sorted.map((row) => (
                <TableRow key={row.id} interactive onClick={() => navigate(`/logs/api-key-requests/${row.id}`)}>
                  {table.columns.visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                      style={column.cellStyle}
                    >
                      {column.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cursor !== null && (
            <div className="flex justify-center p-4">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="rounded-md border border-border px-3 py-1.5 text-base-sm text-text-secondary hover:bg-hover-bg disabled:opacity-50"
              >
                {loadingMore ? t('load-more-loading') : t('load-more')}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
